import {
  CustomerIntakeFormConfig,
  CustomerIntakeFormValues,
  CustomerIntakeResponse,
  CustomerIntakeTemplateVersion,
} from '../types/CustomerIntake.type';
import { saveAs } from 'file-saver';

import { apiDelete, apiGet, apiPatch, apiPost } from './apiClient';

type ApiEnvelope<T> = {
  status: number;
  message: string;
  data: T;
  metadata?: CustomerIntakePaginationMetadata;
};

export type CustomerIntakePaginationMetadata = {
  totalItems: number;
  currentPage: number;
  totalPages: number;
  limit: number;
};

type ApiDefinition = CustomerIntakeFormConfig & {
  schemaVersion: 1;
};

type ApiVersion = {
  id: number;
  version: string;
  versionMajor: number;
  versionMinor: number;
  templateName: string;
  publicTitle: string;
  status: CustomerIntakeTemplateVersion['status'];
  isLocked: boolean;
  definition?: ApiDefinition;
  responseCount: number;
  createdAt: string;
  updatedAt: string;
};

export type CustomerIntakePublicForm = {
  formKey: string;
  publicToken: string;
  publicUrl: string;
  version: string;
  title: string;
  description: string;
  estimatedMinutes: number;
  privacyPolicyVersion: string;
  privacyPolicyUrl: string;
  definition: CustomerIntakeFormConfig;
};

// One intake form (a tab in the templates screen). Each form has its own public
// token → its own link + QR code.
export type CustomerIntakeForm = {
  id: number;
  key: string;
  name: string;
  publicToken: string;
  publicUrl: string;
  versionCount: number;
  activeVersion: {
    id: number;
    version: string;
    publicTitle: string;
  } | null;
  createdAt: string;
  updatedAt: string;
};

export type CustomerIntakeResponseListItem = {
  id: number;
  customerName: string | null;
  phone: string | null;
  email: string | null;
  submittedAt: string;
  conversionStatus: {
    trialBooking: boolean;
    registration: boolean;
  };
};

export type CustomerIntakeResponseOverviewListItem =
  CustomerIntakeResponseListItem & {
    versionId: number;
    version: string;
    templateName: string;
    status: CustomerIntakeTemplateVersion['status'];
  };

export type CustomerIntakeResponseDetail = CustomerIntakeResponseListItem & {
  versionId: number;
  version: string;
  privacyPolicyVersion: string | null;
  consentAcceptedAt: string | null;
  source: string | null;
  sections: Array<{
    sectionId: string;
    title: string;
    description: string | null;
    fields: Array<{
      fieldId: string;
      label: string;
      type: CustomerIntakeFormConfig['sections'][number]['fields'][number]['type'];
      value: string | string[] | boolean | number | null;
      displayValue: string | null;
    }>;
  }>;
};

export type CustomerIntakeConversionPackage = {
  packageId: number;
  packageName: string;
  serviceTypeId: number;
  serviceTypeName: string;
  subtypeId: number;
  subtypeName: string;
  serviceFor: string;
  price: number;
  priceForChild: number | null;
  duration: number;
  visitType: string;
  visitBalance: number;
  trialAvailable: boolean;
};

export type CustomerIntakeConversionPrepare = {
  status:
    | 'READY'
    | 'NEEDS_PHONE'
    | 'NEEDS_NAME'
    | 'NEEDS_DATE_OF_BIRTH'
    | 'SERVICE_BINDING_REQUIRED'
    | 'PACKAGE_REQUIRED'
    | 'PACKAGE_UNAVAILABLE';
  profile: {
    fullName: string | null;
    firstName: string;
    lastName: string;
    email: string | null;
    phone: string | null;
    phoneNormalized: string | null;
    parent: string | null;
    address: string | null;
    dob: string | null;
    gender: 'MALE' | 'FEMALE' | 'UNDEFINED';
  };
  user: {
    id: number;
    fullName: string;
    firstName: string;
    lastName: string;
    email: string | null;
    phone: string | null;
    dob: string;
    userType: string;
    gender: 'MALE' | 'FEMALE' | 'UNDEFINED';
  } | null;
  match: {
    matchedBy: 'PHONE' | 'PHONE_LATEST_CREATED' | 'CREATED' | 'LINKED';
    candidateCount: number;
  } | null;
  packages: CustomerIntakeConversionPackage[];
  selectedPackage: CustomerIntakeConversionPackage | null;
  completedConversions: Array<{
    id: number;
    action: 'TRIAL_BOOKING' | 'REGISTRATION';
    status: 'COMPLETED';
    packageId: number;
    trialBookingId: number | null;
    registrationId: number | null;
    userId: number;
    createdAt: string;
  }>;
};

const normalizeDefinition = (
  definition: ApiDefinition | undefined,
  fallbackTitle: string,
): CustomerIntakeFormConfig => ({
  title: definition?.title || fallbackTitle,
  description: definition?.description || '',
  estimatedMinutes: definition?.estimatedMinutes || 1,
  serviceBindings: definition?.serviceBindings || [],
  sections: definition?.sections || [],
});

const toUiVersion = (version: ApiVersion): CustomerIntakeTemplateVersion => ({
  id: String(version.id),
  name: version.templateName,
  version: version.version.replace(/^v/, ''),
  status: version.status,
  isLocked: version.isLocked,
  responseCount: version.responseCount,
  updatedAt: version.updatedAt,
  ...normalizeDefinition(version.definition, version.publicTitle),
});

const toApiDefinition = (
  template: CustomerIntakeTemplateVersion,
): ApiDefinition => ({
  schemaVersion: 1,
  title: template.title,
  description: template.description,
  estimatedMinutes: template.estimatedMinutes,
  serviceBindings: template.serviceBindings || [],
  sections: template.sections,
});

const unwrap = <T>(response: ApiEnvelope<T>) => response.data;

const toVersionId = (id: string) => {
  const versionId = Number(id);
  if (!Number.isInteger(versionId) || versionId <= 0) {
    throw new Error('Invalid customer intake version ID.');
  }
  return versionId;
};

// ==============================
// Form-level API (multi-form). Each form is a tab; versions belong to a form.
// ==============================
export const listCustomerIntakeForms = async () => {
  const response = await apiGet<ApiEnvelope<CustomerIntakeForm[]>>(
    '/customer-intake/forms',
  );
  return response.data;
};

export const createCustomerIntakeForm = async (name: string) => {
  const response = await apiPost<
    ApiEnvelope<CustomerIntakeForm>,
    { name: string }
  >('/customer-intake/forms', { name });
  return unwrap(response);
};

export const updateCustomerIntakeForm = async (
  formId: number,
  update: { name?: string; archived?: boolean },
) => {
  const response = await apiPatch<
    ApiEnvelope<CustomerIntakeForm>,
    typeof update
  >(`/customer-intake/forms/${formId}`, update);
  return unwrap(response);
};

export const listCustomerIntakeTemplates = async (params?: {
  page?: number;
  limit?: number;
  status?: CustomerIntakeTemplateVersion['status'] | 'ALL';
  search?: string;
  formId?: number;
}) => {
  const query = new URLSearchParams();
  query.set('page', String(params?.page || 1));
  query.set('limit', String(params?.limit || 100));
  query.set('status', params?.status || 'ALL');
  query.set('search', params?.search || '');
  if (params?.formId) query.set('formId', String(params.formId));

  const response = await apiGet<
    ApiEnvelope<ApiVersion[]> & { metadata: CustomerIntakePaginationMetadata }
  >(`/customer-intake/templates?${query.toString()}`);

  return {
    items: response.data.map(toUiVersion),
    metadata: response.metadata,
  };
};

export const getCustomerIntakeTemplate = async (id: string) => {
  const response = await apiGet<ApiEnvelope<ApiVersion>>(
    `/customer-intake/templates/${toVersionId(id)}`,
  );
  return toUiVersion(unwrap(response));
};

export const createCustomerIntakeTemplate = async (
  template: CustomerIntakeTemplateVersion,
  options?: { cloneFromVersionId?: string; formId?: number },
) => {
  const payload: {
    templateName: string;
    publicTitle: string;
    definition?: ApiDefinition;
    cloneFromVersionId?: number;
    formId?: number;
  } = {
    templateName: template.name,
    publicTitle: template.title,
  };

  if (options?.formId) {
    payload.formId = options.formId;
  }

  if (options?.cloneFromVersionId) {
    payload.cloneFromVersionId = toVersionId(options.cloneFromVersionId);
  } else {
    payload.definition = toApiDefinition(template);
  }

  const response = await apiPost<ApiEnvelope<ApiVersion>, typeof payload>(
    '/customer-intake/templates',
    payload,
  );
  return toUiVersion(unwrap(response));
};

export const updateCustomerIntakeTemplate = async (
  template: CustomerIntakeTemplateVersion,
) => {
  const response = await apiPatch<
    ApiEnvelope<ApiVersion>,
    {
      templateName: string;
      publicTitle: string;
      definition: ApiDefinition;
    }
  >(`/customer-intake/templates/${toVersionId(template.id)}`, {
    templateName: template.name,
    publicTitle: template.title,
    definition: toApiDefinition(template),
  });
  return toUiVersion(unwrap(response));
};

export const deleteCustomerIntakeTemplate = async (id: string) => {
  await apiDelete<ApiEnvelope<null>>(
    `/customer-intake/templates/${toVersionId(id)}`,
  );
};

export const activateCustomerIntakeTemplate = async (id: string) => {
  const response = await apiPost<
    ApiEnvelope<ApiVersion>,
    Record<string, never>
  >(`/customer-intake/templates/${toVersionId(id)}/activate`, {});
  return toUiVersion(unwrap(response));
};

export const getCustomerIntakePublicLink = async () => {
  const response = await apiGet<
    ApiEnvelope<{
      formKey: string;
      publicToken: string;
      publicUrl: string;
      activeVersion: {
        id: number;
        version: string;
        publicTitle: string;
      };
    }>
  >('/customer-intake/public-link');
  return unwrap(response);
};

const toPublicForm = (form: {
  formKey: string;
  publicToken?: string;
  publicUrl?: string;
  version: string;
  title: string;
  description: string;
  estimatedMinutes: number;
  privacyPolicyVersion: string;
  privacyPolicyUrl: string;
  definition: ApiDefinition;
}): CustomerIntakePublicForm => ({
  formKey: form.formKey,
  publicToken: form.publicToken || '',
  publicUrl: form.publicUrl || '',
  version: form.version,
  title: form.title,
  description: form.description,
  estimatedMinutes: form.estimatedMinutes,
  privacyPolicyVersion: form.privacyPolicyVersion,
  privacyPolicyUrl: form.privacyPolicyUrl,
  definition: normalizeDefinition(form.definition, form.title),
});

export const getCurrentCustomerIntakePublicForm = async () => {
  const response = await apiGet<
    ApiEnvelope<{
      formKey: string;
      publicToken: string;
      publicUrl: string;
      version: string;
      title: string;
      description: string;
      estimatedMinutes: number;
      privacyPolicyVersion: string;
      privacyPolicyUrl: string;
      definition: ApiDefinition;
    }>
  >('/public/customer-intake/forms/current');
  return toPublicForm(unwrap(response));
};

export const getCustomerIntakePublicForm = async (publicToken: string) => {
  const response = await apiGet<
    ApiEnvelope<{
      formKey: string;
      version: string;
      title: string;
      description: string;
      estimatedMinutes: number;
      privacyPolicyVersion: string;
      privacyPolicyUrl: string;
      definition: ApiDefinition;
    }>
  >(`/public/customer-intake/forms/${encodeURIComponent(publicToken)}`);
  return toPublicForm(unwrap(response));
};

export const submitCustomerIntakeResponse = async (
  publicToken: string,
  body: {
    answers: CustomerIntakeFormValues;
    privacyPolicyVersion?: string;
    source?: string;
  },
  idempotencyKey: string,
) => {
  const response = await apiPost<
    ApiEnvelope<{ reference: string; submittedAt: string }>,
    typeof body
  >(
    `/public/customer-intake/forms/${encodeURIComponent(publicToken)}/responses`,
    body,
    { headers: { 'Idempotency-Key': idempotencyKey } },
  );
  return unwrap(response);
};

export const listCustomerIntakeResponses = async (
  templateId: string,
  params?: {
    page?: number;
    limit?: number;
    search?: string;
    from?: string;
    to?: string;
  },
) => {
  const query = new URLSearchParams();
  query.set('page', String(params?.page || 1));
  query.set('limit', String(params?.limit || 100));
  query.set('search', params?.search || '');
  if (params?.from) query.set('from', params.from);
  if (params?.to) query.set('to', params.to);

  const response = await apiGet<
    ApiEnvelope<CustomerIntakeResponseListItem[]> & {
      metadata: CustomerIntakePaginationMetadata;
    }
  >(
    `/customer-intake/templates/${toVersionId(templateId)}/responses?${query.toString()}`,
  );

  return { items: response.data, metadata: response.metadata };
};

export const listAllCustomerIntakeResponses = async (params?: {
  page?: number;
  limit?: number;
  search?: string;
  from?: string;
  to?: string;
  formId?: number;
  versionId?: number;
}) => {
  const query = new URLSearchParams();
  query.set('page', String(params?.page || 1));
  query.set('limit', String(params?.limit || 10));
  query.set('search', params?.search || '');
  if (params?.from) query.set('from', params.from);
  if (params?.to) query.set('to', params.to);
  if (params?.formId) query.set('formId', String(params.formId));
  if (params?.versionId) query.set('versionId', String(params.versionId));

  const response = await apiGet<
    ApiEnvelope<CustomerIntakeResponseOverviewListItem[]> & {
      metadata: CustomerIntakePaginationMetadata;
    }
  >(`/customer-intake/responses?${query.toString()}`);

  return { items: response.data, metadata: response.metadata };
};

// Download all responses matching the current filters as an Excel file.
export const downloadAllCustomerIntakeResponses = async (params?: {
  search?: string;
  formId?: number;
  versionId?: number;
}) => {
  const query = new URLSearchParams();
  if (params?.search) query.set('search', params.search);
  if (params?.formId) query.set('formId', String(params.formId));
  if (params?.versionId) query.set('versionId', String(params.versionId));

  const response = await apiGet(
    `/customer-intake/responses/export?${query.toString()}`,
    { responseType: 'blob' },
  );
  saveAs(
    new Blob([response as unknown as BlobPart], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    }),
    `survey_responses_${new Date().toISOString().slice(0, 10)}.xlsx`,
  );
};

export const getCustomerIntakeResponse = async (
  templateId: string,
  responseId: number,
) => {
  const response = await apiGet<ApiEnvelope<CustomerIntakeResponseDetail>>(
    `/customer-intake/templates/${toVersionId(templateId)}/responses/${responseId}`,
  );
  return unwrap(response);
};

const responseConversionPath = (templateId: string, responseId: number) =>
  `/customer-intake/templates/${toVersionId(templateId)}/responses/${responseId}`;

export const prepareCustomerIntakeResponseConversion = async (
  templateId: string,
  responseId: number,
  packageId?: number,
) => {
  const response = await apiPost<
    ApiEnvelope<CustomerIntakeConversionPrepare>,
    { packageId?: number }
  >(`${responseConversionPath(templateId, responseId)}/prepare-conversion`, {
    ...(packageId ? { packageId } : {}),
  });
  return unwrap(response);
};

export const convertCustomerIntakeResponseToTrialBooking = async (
  templateId: string,
  responseId: number,
  body: { packageId: number; trialDate: string; note?: string },
) => {
  const response = await apiPost<
    ApiEnvelope<{
      existing: boolean;
      conversion: { id: number; trialBookingId: number | null };
    }>,
    typeof body
  >(`${responseConversionPath(templateId, responseId)}/convert/trial-booking`, body);
  return unwrap(response);
};

export const convertCustomerIntakeResponseToRegistration = async (
  templateId: string,
  responseId: number,
  body: {
    packageId: number;
    sos: string;
    note?: string;
    discountPrice?: number;
    amount?: number;
    subAmount?: number;
    paymentMethod?: 'CASH' | 'BANK_TRANSFER' | 'DEPOSIT';
  },
) => {
  const response = await apiPost<
    ApiEnvelope<{
      existing: boolean;
      conversion: { id: number; registrationId: number | null };
    }>,
    typeof body
  >(`${responseConversionPath(templateId, responseId)}/convert/registration`, body);
  return unwrap(response);
};

export const createInitialCustomerIntakeValues = (
  formConfig: CustomerIntakeFormConfig,
): CustomerIntakeFormValues => {
  const values: CustomerIntakeFormValues = {};
  formConfig.sections.forEach((section) => {
    section.fields.forEach((field) => {
      values[field.id] =
        field.type === 'checkbox' ? [] : field.type === 'consent' ? false : '';
    });
  });
  return values;
};

export const getCustomerIntakeErrorMessage = (
  error: unknown,
  fallback: string,
) => {
  const response = (error as { response?: { data?: { message?: string } } })
    .response;
  return (
    response?.data?.message ||
    (error instanceof Error ? error.message : fallback)
  );
};

export const createEmptyCustomerIntakeTemplate =
  (): CustomerIntakeTemplateVersion => ({
    id: 'new',
    name: 'Customer information registration - Draft',
    title: 'Customer information registration',
    description: '',
    estimatedMinutes: 2,
    sections: [
      {
        id: 'basic-information',
        title: 'Basic Information',
        description: 'Please provide your basic information.',
        fields: [
          {
            id: 'fullName',
            systemKey: 'fullName',
            label: 'Full name',
            type: 'text',
            required: true,
            placeholder: 'Enter your full name',
          },
          {
            id: 'email',
            systemKey: 'email',
            label: 'Email',
            type: 'email',
            required: true,
            placeholder: 'Enter your email address',
          },
          {
            id: 'phone',
            systemKey: 'phone',
            label: 'Phone number',
            type: 'phone',
            required: true,
            placeholder: 'Enter your phone number',
          },
          {
            id: 'dateOfBirth',
            systemKey: 'dateOfBirth',
            label: 'Date of birth',
            type: 'date',
            required: true,
          },
          {
            id: 'age',
            systemKey: 'age',
            label: 'Age',
            type: 'number',
            required: true,
          },
          {
            id: 'gender',
            systemKey: 'gender',
            label: 'Gender',
            type: 'select',
            required: true,
            options: [
              { value: 'MALE', label: 'Male' },
              { value: 'FEMALE', label: 'Female' },
              { value: 'OTHER', label: 'Other' },
            ],
          },
          {
            id: 'parentName',
            systemKey: 'parentName',
            label: 'Parent name',
            type: 'text',
            placeholder: 'Enter parent name',
          },
          {
            id: 'parentEmail',
            systemKey: 'parentEmail',
            label: 'Parent email',
            type: 'text',
            placeholder: 'Enter parent email address',
          },
        ],
      },
      {
        id: 'confirmation',
        title: 'Confirmation',
        description:
          'Please confirm that you agree to share the personal information provided in this form. Aquatics HUB will keep your personal data secure and confidential.',
        fields: [
          {
            id: 'informationConsent',
            label:
              'I agree to share the personal information provided in this form with Aquatics HUB and acknowledge its commitment to keeping my personal data secure and confidential.',
            type: 'consent',
            required: true,
          },
        ],
      },
    ],
    version: '1.0',
    status: 'DRAFT',
    isLocked: false,
    responseCount: 0,
    updatedAt: new Date().toISOString(),
  });

export const toCustomerIntakeResponse = (
  response: CustomerIntakeResponseListItem,
  templateId: string,
): CustomerIntakeResponse => ({
  id: String(response.id),
  templateId,
  submittedAt: response.submittedAt,
  customerName: response.customerName || '',
  phone: response.phone || '',
  email: response.email || '',
  answers: {},
});
